import React, { useState, useMemo, useEffect } from "react";
import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaExternalLinkAlt,
  FaBuilding,
  FaSearch,
  FaTimes,
  FaArrowRight
} from "react-icons/fa";
import jobsData from "./jobs.json";

const FONT = "'Inter', sans-serif";

const scheduleColor = (type) => {
  const t = (type || "").toLowerCase();
  if (t.includes("full")) return { bg: "hsl(150, 80%, 96%)", text: "hsl(150, 84%, 26%)", border: "hsl(150, 70%, 86%)" };
  if (t.includes("part")) return { bg: "hsl(215, 90%, 96%)", text: "hsl(215, 85%, 36%)", border: "hsl(215, 80%, 88%)" };
  if (t.includes("intern")) return { bg: "hsl(280, 85%, 96%)", text: "hsl(280, 80%, 46%)", border: "hsl(280, 75%, 88%)" };
  if (t.includes("contract")) return { bg: "hsl(25, 95%, 96%)", text: "hsl(25, 85%, 40%)", border: "hsl(25, 80%, 88%)" };
  return { bg: "hsl(210, 20%, 96%)", text: "hsl(210, 10%, 40%)", border: "hsl(210, 15%, 88%)" };
};

const JobCard = ({ job, onClick, isSelected }) => {
  const schedule = job.detected_extensions?.schedule_type || "";
  const posted = job.detected_extensions?.posted_at || "";
  const colors = scheduleColor(schedule);

  return (
    <div
      onClick={onClick}
      style={{
        background: "white",
        borderRadius: "14px",
        border: isSelected ? "2px solid #3b82f6" : "1px solid #e2e8f0",
        padding: "20px",
        boxShadow: isSelected ? "0 8px 24px rgba(59,130,246,0.08)" : "0 2px 6px rgba(0,0,0,0.02)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: FONT,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        position: "relative"
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.boxShadow = "0 8px 22px rgba(0,0,0,0.06)";
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.borderColor = "#cbd5e1";
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.02)";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.borderColor = "#e2e8f0";
        }
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
        {/* Company Logo */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "10px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden"
          }}
        >
          {job.thumbnail ? (
            <img
              src={job.thumbnail}
              alt={job.company_name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement.innerHTML = `<span style="font-size:20px;color:#94a3b8">🏢</span>`;
              }}
            />
          ) : (
            <FaBuilding style={{ color: "#94a3b8", fontSize: "20px" }} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: "0 0 4px 0",
              fontSize: "16px",
              fontWeight: "700",
              color: "#0f172a",
              lineHeight: "1.3",
              letterSpacing: "-0.015em"
            }}
          >
            {job.title}
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "#475569", fontWeight: "500" }}>
              <FaBuilding style={{ fontSize: "11px", color: "#94a3b8" }} />
              {job.company_name}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "#475569", fontWeight: "500" }}>
              <FaMapMarkerAlt style={{ fontSize: "11px", color: "#94a3b8" }} />
              {job.location}
            </span>
          </div>
        </div>
      </div>

      {/* Tags row */}
      {job.extensions && job.extensions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {job.extensions.slice(0, 3).map((ext, i) => (
            <span
              key={i}
              style={{
                fontSize: "11px",
                fontWeight: "500",
                color: "#64748b",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "2px 8px",
                borderRadius: "6px"
              }}
            >
              {ext}
            </span>
          ))}
          {job.extensions.length > 3 && (
            <span style={{ fontSize: "11px", color: "#94a3b8", alignSelf: "center" }}>
              +{job.extensions.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Card Footer */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #f1f5f9", marginTop: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {posted && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#64748b", fontWeight: "500" }}>
              <FaClock style={{ fontSize: "10px" }} />
              {posted}
            </span>
          )}
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {schedule && (
            <span
              style={{
                fontSize: "10px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: colors.text,
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                padding: "2px 8px",
                borderRadius: "20px"
              }}
            >
              {schedule}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("All");
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    // Add custom Google Fonts
    const id = "inter-font-link";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const fetchJobs = () => {
    setLoading(true);
    try {
      const parsePostedAt = (posted) => {
        if (!posted) return Infinity;
        const str = posted.toLowerCase().trim();
        let match = str.match(/(\d+)\s+hour/);
        if (match) return parseInt(match[1], 10);
        if (str.includes("yesterday")) return 24;
        match = str.match(/(\d+)\s+day/);
        if (match) return parseInt(match[1], 10) * 24;
        match = str.match(/(\d+)\s+week/);
        if (match) return parseInt(match[1], 10) * 24 * 7;
        match = str.match(/(\d+)\s+month/);
        if (match) return parseInt(match[1], 10) * 24 * 30;
        return 999999;
      };

      const jobsList = (jobsData.jobs_results || []).map((job, index) => ({
        ...job,
        id: job.job_id || job.id || `job-${index}`
      }));

      // Sort by recent dates first
      jobsList.sort((a, b) => {
        const timeA = parsePostedAt(a.detected_extensions?.posted_at);
        const timeB = parsePostedAt(b.detected_extensions?.posted_at);
        return timeA - timeB;
      });

      setJobs(jobsList);
      if (jobsList.length > 0) {
        setSelectedJob(jobsList[0]); // Select first job by default
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load local job board listings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const scheduleTypes = useMemo(() => {
    const types = new Set(["All"]);
    jobs.forEach((j) => {
      const t = j.detected_extensions?.schedule_type;
      if (t) types.add(t);
    });
    return [...types];
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter((job) => {
      const matchSearch =
        !search ||
        job.title?.toLowerCase().includes(search.toLowerCase()) ||
        job.company_name?.toLowerCase().includes(search.toLowerCase()) ||
        job.location?.toLowerCase().includes(search.toLowerCase());
      const matchType =
        filterType === "All" || job.detected_extensions?.schedule_type === filterType;
      return matchSearch && matchType;
    });
  }, [jobs, search, filterType]);

  // Adjust selection if current selected job is filtered out
  useEffect(() => {
    if (filtered.length > 0 && !filtered.find(j => j.id === selectedJob?.id)) {
      setSelectedJob(filtered[0]);
    }
  }, [filtered, selectedJob]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily: FONT,
        paddingTop: "80px",
        boxSizing: "border-box"
      }}
    >
      {/* Hero Banner Area */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 70%)",
          padding: "54px 24px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <div style={{ position: "relative", zIndex: 2 }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "800",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#3b82f6",
              background: "rgba(59,130,246,0.1)",
              padding: "6px 16px",
              borderRadius: "20px",
              border: "1px solid rgba(59,130,246,0.2)"
            }}
          >
            SMIK Careers Portal
          </span>
          <h1
            style={{
              fontSize: "clamp(28px, 5vw, 42px)",
              fontWeight: "900",
              color: "white",
              margin: "16px 0 10px",
              letterSpacing: "-0.03em",
              lineHeight: "1.15"
            }}
          >
            Elevate Your Tech Career
          </h1>
          <p
            style={{
              fontSize: "15px",
              color: "#94a3b8",
              fontWeight: "400",
              margin: "0 auto 28px",
              maxWidth: "500px",
              lineHeight: "1.65"
            }}
          >
            Browse curated software development roles from partner startups and enterprises across major hubs.
          </p>

          {/* Search bar */}
          <div
            style={{
              maxWidth: "550px",
              margin: "0 auto",
              position: "relative",
              display: "flex",
              alignItems: "center"
            }}
          >
            <FaSearch
              style={{
                position: "absolute",
                left: "18px",
                color: "#94a3b8",
                fontSize: "16px"
              }}
            />
            <input
              type="text"
              placeholder="Search by role, company name, or city location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 48px 14px 50px",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: FONT,
                borderRadius: "14px",
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.06)",
                color: "white",
                outline: "none",
                backdropFilter: "blur(12px)",
                boxSizing: "border-box",
                transition: "border 0.2s"
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: "16px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#94a3b8",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <FaTimes style={{ fontSize: "14px" }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter and Count Section */}
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "20px 24px 0",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          boxSizing: "border-box"
        }}
      >
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Employment Type:
        </span>
        {scheduleTypes.map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            style={{
              fontSize: "12px",
              fontWeight: "600",
              fontFamily: FONT,
              padding: "6px 14px",
              borderRadius: "20px",
              border: "1px solid",
              cursor: "pointer",
              transition: "all 0.15s",
              borderColor: filterType === type ? "#0f172a" : "#cbd5e1",
              background: filterType === type ? "#0f172a" : "white",
              color: filterType === type ? "white" : "#64748b"
            }}
          >
            {type}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: "13px", fontWeight: "600", color: "#64748b" }}>
          {filtered.length} position{filtered.length !== 1 ? "s" : ""} active
        </span>
      </div>

      {/* Split view: Jobs Grid list (left) & Detail Panel (right) */}
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "20px 24px 48px",
          display: "grid",
          gridTemplateColumns: "1fr 1.2fr",
          gap: "24px",
          boxSizing: "border-box"
        }}
      >
        {/* Left Side: Jobs Cards list */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {loading ? (
            /* Loading states skeleton cards */
            [1, 2, 3].map(i => (
              <div key={i} style={{ height: "140px", borderRadius: "14px", background: "white", border: "1px solid #e2e8f0", padding: "20px", boxSizing: "border-box" }}>
                <div style={{ display: "flex", gap: "14px" }}>
                  <div style={{ width: "48px", height: "48px", borderRadius: "10px", background: "#f1f5f9" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: "16px", background: "#f1f5f9", width: "70%", borderRadius: "4px", marginBottom: "8px" }} />
                    <div style={{ height: "12px", background: "#f1f5f9", width: "40%", borderRadius: "4px" }} />
                  </div>
                </div>
              </div>
            ))
          ) : error ? (
            <div style={{ textAlign: "center", padding: "48px 20px" }}>
              <p style={{ color: "#ef4444", fontWeight: "600" }}>{error}</p>
              <button onClick={fetchJobs} style={{ marginTop: "12px", background: "#0f172a", color: "white", padding: "8px 16px", borderRadius: "8px", border: "none", cursor: "pointer" }}>Reload Board</button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "52px 20px", gridColumn: "1 / -1" }}>
              <FaBriefcase style={{ fontSize: "40px", color: "#cbd5e1", marginBottom: "12px" }} />
              <p style={{ fontSize: "16px", fontWeight: "700", color: "#64748b", margin: 0 }}>No matching jobs found</p>
              <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>Try removing search keywords or filter pills.</p>
            </div>
          ) : (
            filtered.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                isSelected={selectedJob?.id === job.id}
                onClick={() => setSelectedJob(job)}
              />
            ))
          )}
        </div>

        {/* Right Side: Large Detail Drawer Panel */}
        <div style={{ position: "sticky", top: "100px", height: "calc(100vh - 140px)", overflowY: "auto" }}>
          {selectedJob ? (
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                padding: "32px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box"
              }}
            >
              {/* Detail Header */}
              <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", borderBottom: "1px solid #f1f5f9", paddingBottom: "24px" }}>
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "12px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden"
                }}>
                  {selectedJob.thumbnail ? (
                    <img src={selectedJob.thumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <FaBuilding style={{ color: "#94a3b8", fontSize: "24px" }} />
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: "0 0 6px 0", fontSize: "22px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.025em", lineHeight: "1.2" }}>
                    {selectedJob.title}
                  </h2>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "14px", alignItems: "center" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#334155", display: "flex", alignItems: "center", gap: "6px" }}>
                      <FaBuilding style={{ color: "#94a3b8", fontSize: "12px" }} />
                      {selectedJob.company_name}
                    </span>
                    <span style={{ fontSize: "14px", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                      <FaMapMarkerAlt style={{ color: "#94a3b8", fontSize: "12px" }} />
                      {selectedJob.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Badges/Perks Row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", margin: "20px 0" }}>
                <span style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  background: (selectedJob.detected_extensions?.schedule_type || "").toLowerCase().includes("full") ? "#ecfdf5" : "#eff6ff",
                  color: (selectedJob.detected_extensions?.schedule_type || "").toLowerCase().includes("full") ? "#059669" : "#2563eb",
                  border: `1px solid ${(selectedJob.detected_extensions?.schedule_type || "").toLowerCase().includes("full") ? "#a7f3d0" : "#bfdbfe"}`
                }}>
                  {selectedJob.detected_extensions?.schedule_type || "Full-time"}
                </span>
                
                {selectedJob.extensions?.map((ext, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontSize: "11px",
                      background: "#f1f5f9",
                      color: "#475569",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontWeight: "500"
                    }}
                  >
                    {ext}
                  </span>
                ))}
              </div>

              {/* Description Body */}
              <div style={{ flex: 1, overflowY: "auto", paddingRight: "8px" }}>
                <h4 style={{ margin: "0 0 10px 0", fontSize: "14px", fontWeight: "700", color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Job Description
                </h4>
                <div
                  style={{
                    fontSize: "14px",
                    lineHeight: "1.8",
                    color: "#334155",
                    whiteSpace: "pre-line",
                    fontFamily: FONT
                  }}
                >
                  {selectedJob.description}
                </div>
              </div>

              {/* Apply options footer drawer */}
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "20px", marginTop: "20px" }}>
                <span style={{ display: "block", fontSize: "12px", color: "#64748b", fontWeight: "600", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Apply links
                </span>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  {selectedJob.apply_options && selectedJob.apply_options.length > 0 ? (
                    selectedJob.apply_options.map((opt, i) => (
                      <a
                        key={i}
                        href={opt.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "13px",
                          fontWeight: "700",
                          textDecoration: "none",
                          padding: "10px 20px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "all 0.15s",
                          background: i === 0 ? "#2563eb" : "#f1f5f9",
                          color: i === 0 ? "white" : "#334155",
                          boxShadow: i === 0 ? "0 4px 10px rgba(37,99,235,0.15)" : "none"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.opacity = "0.9";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.opacity = "1";
                        }}
                      >
                        Apply on {opt.title} <FaExternalLinkAlt style={{ fontSize: "10px" }} />
                      </a>
                    ))
                  ) : (
                    <div style={{ fontSize: "13px", color: "#ef4444", fontWeight: "600" }}>
                      No apply links provided for this posting.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#94a3b8"
              }}
            >
              <FaBriefcase style={{ fontSize: "48px", color: "#e2e8f0", marginBottom: "12px" }} />
              <p style={{ margin: 0, fontWeight: "600" }}>Select a job card to view complete specifications.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
